import { useEffect, useState } from "react";
import { fetchPrices } from "@/lib/api";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";

const ITEMS = [
    { key: "karat_24", label: "24" },
    { key: "karat_22", label: "22" },
    { key: "karat_21", label: "21" },
    { key: "karat_18", label: "18" },
];

const STABILITY_USD = 20;

// Gold spot market is essentially open Sun 22:00 UTC → Fri 22:00 UTC (continuous
// hand-off across Sydney/Tokyo/London/New York), closed Fri 22:00 → Sun 22:00 UTC.
function getMarketStatus(now = new Date()) {
    const day = now.getUTCDay(); // 0=Sun … 6=Sat
    const hour = now.getUTCHours();
    let isOpen;
    if (day === 6) isOpen = false; // Saturday
    else if (day === 0) isOpen = hour >= 22; // Sunday open at 22:00 UTC
    else if (day === 5) isOpen = hour < 22; // Friday close at 22:00 UTC
    else isOpen = true;

    // Compute the next transition instant.
    const next = new Date(now);
    if (isOpen) {
        // Next close = coming Friday 22:00 UTC (or today if Friday <22:00)
        const daysToFri = (5 - day + 7) % 7;
        next.setUTCDate(now.getUTCDate() + daysToFri);
        next.setUTCHours(22, 0, 0, 0);
        if (next <= now) next.setUTCDate(next.getUTCDate() + 7);
    } else {
        // Next open = next Sunday 22:00 UTC
        if (day === 0) {
            // Sunday but before 22:00 → same day 22:00
            next.setUTCHours(22, 0, 0, 0);
        } else {
            const daysToSun = (7 - day) % 7 || 7;
            next.setUTCDate(now.getUTCDate() + daysToSun);
            next.setUTCHours(22, 0, 0, 0);
        }
    }
    return { isOpen, msUntilTransition: next - now, nextAt: next };
}

function fmtCountdown(ms) {
    const s = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(s / 86400);
    const h = String(Math.floor((s % 86400) / 3600)).padStart(2, "0");
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    const dayLabel = days === 0 ? "" : days === 1 ? "يوم و " : days === 2 ? "يومين و " : `${days} أيام و `;
    return { dayLabel, clock: `${h}:${m}:${sec}` };
}

const KSA = "Asia/Riyadh";
const fmtDay = (d) => d.toLocaleDateString("ar-SA", { weekday: "long", timeZone: KSA });
const fmtTime = (d) =>
    d.toLocaleTimeString("ar-SA-u-nu-latn", { hour: "numeric", minute: "2-digit", timeZone: KSA });

export default function PriceTicker() {
    const [d, setD] = useState(null);
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const load = () => fetchPrices().then(setD).catch(() => {});
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, []);

    // Second-by-second clock for the countdown / live feel.
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const fmt = (v) =>
        v?.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }) ?? "—";

    // Trend from absolute USD move on the ounce (±$20 = stable).
    const changePct = d?.change_pct ?? 0;
    const ounce = d?.ounce_usd ?? 0;
    const changeUsd = (ounce * changePct) / 100;
    let trend = "stable";
    if (changeUsd > STABILITY_USD) trend = "up";
    else if (changeUsd < -STABILITY_USD) trend = "down";
    const trendLabel =
        trend === "up" ? "ارتفاع" : trend === "down" ? "هبوط" : "استقرار";
    const trendColor =
        trend === "up"
            ? "text-emerald-300"
            : trend === "down"
            ? "text-red-300"
            : "text-white";
    const TrendIcon =
        trend === "up" ? ArrowUp : trend === "down" ? ArrowDown : Minus;

    const { isOpen, msUntilTransition, nextAt } = getMarketStatus(now);
    const countdown = fmtCountdown(msUntilTransition);

    return (
        <div
            data-testid="price-ticker"
            className="bg-gradient-to-l from-[#0F2A28] via-[#1E5E5A] to-[#0F2A28] text-white border-b-2 border-[#4FB8B0]"
        >
            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 md:py-6">
                {/* Status + LIVE + Trend row (spans across on desktop) */}
                <div className="flex items-center justify-between gap-3 mb-3 md:mb-4">
                    <div className="flex items-center gap-2 md:gap-3 leading-tight">
                        <span
                            data-testid="market-status"
                            className={`text-[11px] md:text-sm font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
                                isOpen
                                    ? "bg-emerald-500/20 text-emerald-200"
                                    : "bg-red-500/25 text-red-200"
                            }`}
                        >
                            {isOpen ? "مفتوح" : "مغلق"}
                        </span>
                        <span className="font-bold text-[#F2E8D5] text-sm md:text-lg uppercase tracking-widest">
                            مباشر
                        </span>
                    </div>
                    <div
                        className={`inline-flex flex-col items-end leading-tight ${trendColor}`}
                        data-testid="trend-block"
                    >
                        <span className="text-[10px] md:text-xs font-bold uppercase tracking-wider opacity-90">
                            {trendLabel}
                        </span>
                        <span className="inline-flex items-center gap-1 font-bold text-sm md:text-base font-numeric">
                            <TrendIcon size={18} />
                            {Math.abs(changePct).toFixed(2)}%
                        </span>
                    </div>
                </div>

                <div
                    data-testid="market-countdown"
                    className="mb-3 md:mb-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-[12px] md:text-sm font-bold text-[#F2E8D5] bg-black/25 rounded px-3 py-1.5"
                >
                    <span data-testid="market-next-label">
                        {isOpen ? "سيتم إغلاق التداول" : "سيتم فتح التداول"} يوم {fmtDay(nextAt)} الساعة{" "}
                        <span className="font-numeric">{fmtTime(nextAt)}</span>
                    </span>
                    <span data-testid="market-countdown-timer" className="inline-flex items-center gap-1">
                        بعد {countdown.dayLabel}
                        <span dir="ltr" className="font-numeric font-black text-white text-base md:text-lg">
                            {countdown.clock}
                        </span>
                    </span>
                </div>

                {/* Karats + Ounce — single row, ounce first on the right (RTL) */}
                <div className="flex flex-nowrap items-end justify-between gap-x-3 md:gap-x-4 overflow-x-auto no-scrollbar">
                    {/* Ounce — visually first (on the right in RTL) using CSS order */}
                    <span
                        data-testid="ounce-price"
                        className="font-numeric flex flex-col items-start leading-tight shrink-0 order-[-1]"
                    >
                        <span className="text-white text-sm md:text-xl font-bold tracking-wider mb-1">
                            الأونصة{" "}
                            <span className="text-[#A8D5D0] text-[10px] md:text-sm font-normal">
                                USD
                            </span>
                        </span>
                        <span dir="ltr" className="font-black text-[#F2E8D5] text-lg md:text-3xl">
                            $ {fmt(d?.ounce_usd)}
                        </span>
                    </span>
                    {ITEMS.map((it) => (
                        <span
                            key={it.key}
                            className="font-numeric flex flex-col items-start leading-tight shrink-0"
                        >
                            <span className="text-white text-sm md:text-xl font-bold tracking-wider mb-1">
                                عيار{" "}
                                <span className="font-black text-[#4FB8B0] text-xl md:text-4xl align-middle">
                                    {it.label}
                                </span>
                            </span>
                            <span className="font-black text-white text-lg md:text-3xl">
                                {fmt(d?.[it.key])}
                            </span>
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}
